/**
 * Scout Audio Player Component
 * Plays Scout's voice messages with avatar display
 */

import { useState, useRef, useEffect } from 'react'
import { Play, Pause, Volume2 } from 'lucide-react'
import { motion } from 'framer-motion'

export default function ScoutAudioPlayer({ audioUrl, scoutAvatar, expression = 'friendly', autoPlay = false }) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const audioRef = useRef(null)

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const handleLoadedMetadata = () => setDuration(audio.duration)
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime)
    const handleEnded = () => setIsPlaying(false)

    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('ended', handleEnded)

    if (autoPlay) {
      audio.play().then(() => setIsPlaying(true)).catch(() => {})
    }

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('ended', handleEnded)
    }
  }, [audioUrl, autoPlay])

  const togglePlay = () => {
    const audio = audioRef.current
    if (isPlaying) {
      audio.pause()
    } else {
      audio.play()
    }
    setIsPlaying(!isPlaying)
  }

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00'
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  return (
    <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border-2 border-orange-200">
      {/* Scout Avatar */}
      {scoutAvatar && (
        <motion.div
          animate={isPlaying ? { scale: [1, 1.05, 1] } : {}}
          transition={{ duration: 0.6, repeat: isPlaying ? Infinity : 0 }}
          className="flex-shrink-0"
        >
          <div className="relative w-16 h-16 rounded-full overflow-hidden border-4 border-orange-300 shadow-lg">
            <img
              src={scoutAvatar}
              alt={`Scout ${expression}`}
              className="w-full h-full object-cover"
            />
            {isPlaying && (
              <div className="absolute inset-0 bg-orange-400 opacity-20 animate-pulse" />
            )}
          </div>
        </motion.div>
      )}

      {/* Audio Controls */}
      <div className="flex-1">
        <div className="flex items-center gap-3">
          {/* Play/Pause Button */}
          <button
            onClick={togglePlay}
            className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-orange-500 hover:bg-orange-600 text-white transition-colors shadow-md"
          >
            {isPlaying ? (
              <Pause className="w-5 h-5" fill="currentColor" />
            ) : (
              <Play className="w-5 h-5 ml-0.5" fill="currentColor" />
            )}
          </button>

          {/* Progress Bar */}
          <div className="flex-1">
            <div className="h-2 bg-orange-200 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-orange-500"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.1 }}
              />
            </div>
            <div className="flex justify-between items-center mt-1 text-xs text-orange-700">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          {/* Volume Icon */}
          <Volume2 className="w-4 h-4 text-orange-500" />
        </div>
      </div>

      {/* Hidden Audio Element */}
      <audio ref={audioRef} src={audioUrl} preload="metadata" />
    </div>
  )
}
